"use client";

import React from "react";
import { Phone, MessageCircle, Mail, MapPin, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const contactMethods = [{
  id: "phone",
  title: "מוקד טלפוני",
  description: "מענה אנושי ומקצועי 24/7 לתיאום מיידי של מטפל סיעודי.",
  value: "073-1234567",
  icon: Phone,
  href: "tel:+972731234567",
  actionText: "התקשרו עכשיו",
  glowClass: "from-primary/10 via-primary/5 to-transparent",
  borderHover: "hover:border-primary/50",
  iconHover: "group-hover:text-primary"
}, {
  id: "whatsapp",
  title: "WhatsApp",
  description: "שלחו הודעה מהירה לתיאום מטפל לבית או לבית החולים.",
  value: "050-1234567",
  icon: MessageCircle,
  href: "https://wa.me/972501234567",
  actionText: "שלחו הודעה",
  glowClass: "from-[#10B981]/10 via-[#10B981]/5 to-transparent",
  borderHover: "hover:border-[#10B981]/50",
  iconHover: "group-hover:text-[#10B981]"
}, {
  id: "email",
  title: "דואר אלקטרוני",
  description: "לפניות בנושאי שיתופי פעולה, זכויות סיעוד ומידע נוסף.",
  value: "office@siudy.net",
  icon: Mail,
  href: "mailto:office@siudy.net",
  actionText: "שלחו אימייל",
  glowClass: "from-secondary/10 via-secondary/5 to-transparent",
  borderHover: "hover:border-secondary/50",
  iconHover: "group-hover:text-secondary"
}];
export default function ContactUs() {
  return <section data-section-id="189128" id="אנחנו-כאן-בשבילך" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none select-none">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/light_global_reach_map.png)] bg-cover bg-center opacity-5 mix-blend-multiply" />
        {}
        <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: `linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)`,
        backgroundSize: "4rem 4rem"
      }} />
        {}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-primary/5 blur-[120px] rounded-full opacity-50" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-background bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent to-background" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 max-w-6xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-secondary-foreground text-sm font-medium mb-6">
            <MapPin className="w-4 h-4" />
            <span>פריסה ארצית מלאה - הגעה תוך 5 שעות בלבד</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tight mb-6 text-foreground">
            אנחנו כאן בשבילך <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              בכל רגע ובכל מקום
            </span>
          </h2>
          <p className="text-lg font-default text-muted-foreground">
            זקוקים למטפל סיעודי באופן מיידי? הצוות שלנו זמין עבורכם לתיאום מהיר,
            מקצועי ואנושי של מטפלים ישראלים מנוסים.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {contactMethods.map((method, i) => <Card key={method.id} data-index={i} className={`group relative overflow-hidden bg-card/60 backdrop-blur-xl border-border/50 transition-all duration-500 ${method.borderHover} hover:-translate-y-1 shadow-xl shadow-primary/5`}>
              {}
              <div className={`absolute inset-0 bg-gradient-to-br ${method.glowClass} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
              <div className="absolute inset-0 shadow-[inset_0_0_30px_rgba(0,0,0,0.01)] pointer-events-none rounded-xl" />

              <CardContent className="p-8 flex flex-col items-center text-center relative z-10 h-full">
                <div className="w-16 h-16 rounded-2xl bg-background/80 border border-border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-sm">
                  <method.icon className={`w-8 h-8 text-muted-foreground transition-colors duration-300 ${method.iconHover}`} />
                </div>

                <h3 className="text-2xl font-default font-semibold mb-3 text-foreground">
                  {method.title}
                </h3>
                <p className="text-muted-foreground font-default text-sm mb-6 flex-grow">
                  {method.description}
                </p>

                <div className="w-full space-y-4 mt-auto">
                  <div className="text-lg font-default font-medium tracking-wide text-foreground/90 bg-muted/30 py-2 px-4 rounded-lg border border-border/50">
                    {method.value}
                  </div>

                  <Button asChild variant="outline" className={`w-full rounded-xl border-border/80 bg-background/80 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-all duration-300 group/btn`}>
                    <Link to={method.href} newTab={method.id === "whatsapp"} className="flex items-center justify-center gap-2">
                      <span className="font-default">{method.actionText}</span>
                      <ArrowRight className="w-4 h-4 opacity-50 group-hover/btn:opacity-100 group-hover/btn:translate-x-1 transition-all" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}